/**
 * @file discardDataAggregator.js
 * @description Funções para processamento e agregação dos dados de leads descartados
 */

import { hasValidWhatsApp } from '../components/Filters/phoneValidation'

/**
 * Agrega dados de leads descartados por motivo de descarte para visualizações gráficas
 * 
 * Esta função processa um array de objetos de leads, filtra apenas aqueles com situação 
 * "Descartado" e os agrupa por motivo de descarte. Retorna os dados em formato adequado
 * para uso em gráficos (como gráficos de pizza ou barras).
 * 
 * @param {Array<Object>} chartData - Array de objetos representando todos os leads
 * @param {string} chartData[].situacaoInterna - A situação/status interno de cada lead
 * @param {string} [chartData[].motivoDescarte] - O motivo do descarte (quando aplicável)
 * @returns {Object} Dados agregados para uso em gráficos
 * @returns {Array<string>} .labels - Array com os diferentes motivos de descarte
 * @returns {Array<number>} .data - Array com as contagens de leads para cada motivo
 * 
 * @example
 * // Exemplo de uso:
 * const leads = [
 *   { situacaoInterna: "Em Qualificação" }, // Será ignorado (não é descartado)
 *   { situacaoInterna: "Descartado", motivoDescarte: "Sem orçamento" },
 *   { situacaoInterna: "Descartado", motivoDescarte: "Sem orçamento" },
 *   { situacaoInterna: "Descartado", motivoDescarte: "Escolheu concorrente" },
 *   { situacaoInterna: "Descartado" } // Sem motivo especificado
 * ];
 * const result = aggregateDiscardData(leads);
 * // result = {
 * //   labels: ["Sem orçamento", "Escolheu concorrente", "Sem Motivo"],
 * //   data: [2, 1, 1]
 * // }
 */
export function aggregateDiscardData(chartData) {
  // Filtra apenas os leads com situação "Descartado"
  // Se chartData não for um array, usa um array vazio como fallback
  const discardLeads = Array.isArray(chartData)
    ? chartData.filter(lead => lead.situacaoInterna === "Descartado")
    : [];

  // Objeto para agrupar leads por motivo de descarte
  // Serve como contador para cada categoria de motivo
  const group = {};

  discardLeads.forEach((lead) => {
    // Extrai o motivo de descarte, ou usa "Sem Motivo" se não estiver definido
    // Isso garante que leads sem motivo explícito também sejam contabilizados
    const motivo = lead.motivoDescarte || "Sem Motivo";

    const validWhatsApp = hasValidWhatsApp(lead);

    // Só processa leads com WhatsApp válido
    if (validWhatsApp) {
      // Incrementa o contador para este motivo
      // Se é a primeira ocorrência, inicializa com 0 e adiciona 1
      // Se já existe, simplesmente incrementa o valor existente
      group[motivo] = (group[motivo] || 0) + 1;
    }
  });

  // Extrai os nomes dos motivos (chaves do objeto) para usar como rótulos
  const labels = Object.keys(group);

  // Mapeia cada rótulo para seu valor correspondente no objeto group
  // Isso mantém os arrays labels e data sincronizados na mesma ordem
  const data = labels.map(label => group[label]);

  // Retorna um objeto com arrays de rótulos e dados prontos para uso em gráficos
  // Este formato é compatível com bibliotecas comuns de visualização de dados
  return { labels, data };
}